import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { IconAlertHexagon, loaderGIF, SelectedClose } from '../../../../../assets/images';
import { REACT_APP_GROUP_API_URL_RESOLVED, REACT_APP_API_URL } from '../../../../../Components/processENV';
import { ActiveChatAction } from '../../../../../Actions/RecentChatActions';
import { GroupsDataAction } from '../../../../../Actions/GroupsAction';
import { chatResetMessage } from '../../../../../Actions/GroupChatMessageActions';
import { getGroupData } from '../../../../../Helpers/Chat/Group';
import { formatUserIdToJid } from '../../../../../Helpers/Chat/User';
import { CHAT_TYPE_GROUP } from '../../../../../Helpers/Chat/Constant';
import { getFromLocalStorageAndDecrypt } from '../../../../../Components/WebChat/WebChatEncryptDecrypt';
import SDK from '../../../../../Components/SDK';
import Store from '../../../../../Store';
import { resetActionModal } from '../../../../utils';
import { showSucessToast, showErrorToast } from '../../../../utils';
import { getAppOnlineStatus } from '../../../../../Slice';
import { NO_INTERNET } from '../../../../utils/constants';
import toastr from 'toastr';
import './GroupJoinRequest.scss';

const NAVIGATION_DELAY_MS = 500;
const RETRY_DELAY_MS = 500;

const extractGroupAccessUri = (uri) => {
   if (!uri) return uri;
   
   if (uri.includes('http://') || uri.includes('https://')) {
      try {
         const url = new URL(uri);
         const pathParts = url.pathname.split('/');
         const groupIndex = pathParts.indexOf('group');
         if (groupIndex !== -1 && pathParts[groupIndex + 1]) {
            return pathParts[groupIndex + 1];
         }
      } catch (err) {
         if (uri.includes('/group/')) {
            const parts = uri.split('/group/');
            return parts[parts.length - 1];
         }
      }
   }
   
   return uri;
};

const createGroupChatData = (groupInfo, groupFromList) => {
   const groupJid = formatUserIdToJid(groupInfo.groupId, CHAT_TYPE_GROUP);
   return {
      roster: {
         groupId: groupInfo.groupId,
         groupName: groupFromList?.groupName || groupInfo.groupName || groupInfo.name || '',
         groupImage: groupFromList?.groupImage || groupInfo.groupImage || '',
         ...(groupFromList && { ...groupFromList }),
      },
      recent: {
         chatType: 'groupchat',
         fromUserId: groupInfo.groupId,
      },
      chatType: 'groupchat',
      chatId: groupInfo.groupId,
      chatJid: groupJid,
   };
};

const openGroupConversation = (groupId, groupChatData) => {
   Store.dispatch(chatResetMessage())
      .then(() => {
         Store.dispatch(ActiveChatAction(groupChatData));
         
         setTimeout(() => {
            const sanitizedGroupId = CSS.escape(groupId);
            const recentChatItem = document.querySelector(`[data-chat-id="${sanitizedGroupId}"]`);
            if (recentChatItem) {
               recentChatItem.click();
            } else {
               const conversationSection = document.querySelector('.chat-conversion');
               if (conversationSection && !conversationSection.classList.contains('popup-open')) {
                  conversationSection.classList.add('popup-open');
               }
               
               const activeChat = Store.getState().activeChatData?.data;
               if (!activeChat || activeChat.chatId !== groupId) {
                  Store.dispatch(ActiveChatAction(groupChatData));
               }
            }
         }, RETRY_DELAY_MS);
      })
      .catch(() => {
         Store.dispatch(ActiveChatAction(groupChatData));
      });
};

const CloseButton = memo(({ onClick }) => (
   <button
      className="close_popup"
      onClick={onClick}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      aria-label="Close popup">
      <SelectedClose />
   </button>
));

CloseButton.displayName = 'CloseButton';
CloseButton.propTypes = {
   onClick: PropTypes.func.isRequired,
};

const LoadingSpinner = memo(() => (
   <div className="loader-wrapper">
      <img src={loaderGIF} alt="Loading..." style={{ width: '50px', height: '50px' }} />
   </div>
));

LoadingSpinner.displayName = 'LoadingSpinner';

const ErrorModal = memo(({ error, onClose }) => (
   <div className="alert-modal-backdrop">
      <div className="alert-modal-content" style={{ width: '400px' }}>
         <div className="alert-modal-icon">
            <IconAlertHexagon />
         </div>
         <h3 className="alert-modal-title">OOPS!!</h3>
         <p className="alert-modal-description">{error.message || 'Something went wrong'}</p>
         <div className="alert-modal-buttons-container">
            <div className="alert-modal-buttons">
               <button className="alert-button" onClick={onClose}>
                  CLOSE
               </button>
            </div>
         </div>
      </div>
   </div>
));

ErrorModal.displayName = 'ErrorModal';
ErrorModal.propTypes = {
   error: PropTypes.object.isRequired,
   onClose: PropTypes.func.isRequired,
};

const GroupCard = memo(({ groupInfo, onJoin }) => {
   const groupName = groupInfo?.groupName || groupInfo?.name || 'Untitled Group';
   const groupImage = groupInfo?.groupImage || groupInfo?.imageUri || '';
   const description = groupInfo?.description || groupInfo?.groupDescription || '';

   const handleButtonClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onJoin(e);
   };

   return (
      <div className="group-card" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
         {groupImage && (
            <div className="group-avatar">
               <img
                  src={groupImage}
                  alt={groupName}
                  onError={(e) => {
                     e.target.onerror = null;
                     e.target.style.display = 'none';
                  }}
               />
            </div>
         )}
         {!groupImage && (
            <div className="group-avatar">
               <div className="avatar-text">{groupName.charAt(0).toUpperCase()}</div>
            </div>
         )}

         <div className="group-info">
            <h3 className="group-name">{groupName}</h3>
            {description && <p className="group-description">{description}</p>}
         </div>

         <button type="button" className="join-button" onClick={handleButtonClick}>
            Join Group
         </button>
      </div>
   );
});

GroupCard.displayName = 'GroupCard';
GroupCard.propTypes = {
   groupInfo: PropTypes.object.isRequired,
   onJoin: PropTypes.func.isRequired,
};

const GroupJoinRequest = memo(({ accessUri = '', onClose }) => {
   const [groupInfo, setGroupInfo] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);

   useEffect(() => {
      if (!accessUri) {
         setError({ message: 'Access URI is missing' });
         setLoading(false);
         return;
      }

      const controller = new AbortController();
      let timeoutId;

      const fetchGroupInfo = async () => {
         timeoutId = setTimeout(() => controller.abort(), 10000);

         try {
            setLoading(true);
            setError(null);
            
            const groupAccessUri = extractGroupAccessUri(accessUri);

            const apiUrl = `${REACT_APP_GROUP_API_URL_RESOLVED}/group/info?accessUri=${encodeURIComponent(groupAccessUri)}`;

            const response = await fetch(apiUrl, {
               method: 'GET',
               headers: {
                  'Accept': '*/*',
               },
               signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
               const errorData = await response.json().catch(() => ({}));
               setError({ message: errorData?.message || `Failed to fetch group information (${response.status})` });
               setLoading(false);
               return;
            }

            const data = await response.json();

            if (data && (data.status === 200 || data.statusCode === 200)) {
               const apiData = data.data || data;

               setGroupInfo({
                  groupId: apiData.groupId,
                  groupName: apiData.groupTitle || apiData.groupName,
                  groupImage: apiData.groupImage,
                  description: apiData.groupDescription || apiData.description,
                  groupSiteName: apiData.groupSiteName,
                  accessUri: apiData.accessUri || groupAccessUri,
                  member: apiData.member
               });
            } else {
               setError({ message: data?.message || 'Failed to fetch group information' });
            }
         } catch (err) {
            clearTimeout(timeoutId);
            if (err.name === 'AbortError') {
               setError({ message: 'Request timed out. Please try again.' });
            } else {
               setError({ message: 'Network error occurred. Please try again later.' });
            }
         } finally {
            setLoading(false);
         }
      };

      fetchGroupInfo();

      return () => {
         clearTimeout(timeoutId);
         controller.abort();
      };
   }, [accessUri]);

   const handleClosePopup = useCallback(() => {
      if (onClose) {
         onClose();
      } else {
         resetActionModal();
      }
   }, [onClose]);

   const handleJoin = useCallback(async (e) => {
      if (e) {
         e.preventDefault();
         e.stopPropagation();
      }
      
      if (!groupInfo?.groupId) {
         showErrorToast('Group information is missing');
         return;
      }

      if (!getAppOnlineStatus()) {
         toastr.error(NO_INTERNET);
         return;
      }

      try {
         const authUser = getFromLocalStorageAndDecrypt('auth_user');
         const token = getFromLocalStorageAndDecrypt('token');
         
         if (!authUser || !authUser.username) {
            showErrorToast('User authentication is missing');
            return;
         }

         if (!token) {
            showErrorToast('Authorization token is missing');
            return;
         }
         const joinApiUrl = `${REACT_APP_GROUP_API_URL_RESOLVED}/group/joinViaShare`;
         const controller = new AbortController();
         const timeoutId = setTimeout(() => controller.abort(), 10000);
         
         try {
            const joinResponse = await fetch(joinApiUrl, {
               method: 'POST',
               headers: {
                  'username': authUser.username,
                  'userdomain': REACT_APP_API_URL,
                  'Content-Type': 'application/json',
                  'Authorization': token,
               },
               body: JSON.stringify({
                  groupId: groupInfo.groupId,
               }),
               signal: controller.signal,
            });

            clearTimeout(timeoutId);

            const joinData = await joinResponse.json();

            if (joinResponse.ok && (joinData.status === 200 || joinData.statusCode === 200)) {
               if (joinData.data?.joinedSuccessfully) {
                  showSucessToast('You have successfully joined the group');
                  
                  try {
                     const groupListRes = await SDK.getGroupsList();
                     if (groupListRes && groupListRes.statusCode === 200) {
                        Store.dispatch(GroupsDataAction(groupListRes.data));
                     }
                     
                     const recentChatsRes = await SDK.getRecentChatsByPagination();
                     if (recentChatsRes && recentChatsRes.statusCode === 200) {
                        const { RecentChatAction } = await import('../../../../../Actions/RecentChatActions');
                        Store.dispatch(RecentChatAction(recentChatsRes.data));
                     }
                  } catch (err) {
                     // Silently fail - group will still open
                  }
               } else {
                  showSucessToast(joinData.data?.message || joinData.message || 'Opening group...');
               }
               
               handleClosePopup();

               setTimeout(() => {
                  const groupFromList = getGroupData(groupInfo.groupId);
                  const groupChatData = createGroupChatData(groupInfo, groupFromList);
                  openGroupConversation(groupInfo.groupId, groupChatData);
               }, NAVIGATION_DELAY_MS);
            } else {
               showErrorToast(joinData?.message || joinData?.data?.message || 'Failed to join the group');
            }
         } catch (fetchErr) {
            clearTimeout(timeoutId);
            if (fetchErr.name === 'AbortError') {
               showErrorToast('Request timed out. Please try again.');
            } else {
               throw fetchErr;
            }
         }
      } catch (error) {
         showErrorToast('Network error occurred. Please try again.');
      }
   }, [groupInfo, handleClosePopup]);

   const mainContent = useMemo(() => {
      if (loading) {
         return <LoadingSpinner />;
      }

      if (!groupInfo) {
         return null;
      }

      if (groupInfo.member && Object.keys(groupInfo.member).length > 0 && groupInfo.member.status !== 0) {
         const handleOpenGroup = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (!groupInfo.groupId) {
               return;
            }

            handleClosePopup();

            const groupFromList = getGroupData(groupInfo.groupId);
            const groupChatData = createGroupChatData(groupInfo, groupFromList);
            openGroupConversation(groupInfo.groupId, groupChatData);
         };

         return (
            <div className="join-request-popup" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
               <CloseButton onClick={handleClosePopup} />
               <div className="already-member">
                  <p>You are already a member of this group.</p>
                  <button type="button" className="join-button" onClick={handleOpenGroup}>
                     Open Group
                  </button>
               </div>
            </div>
         );
      }

      return (
         <div className="join-request-popup" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleClosePopup} />
            <GroupCard groupInfo={groupInfo} onJoin={handleJoin} />
         </div>
      );
   }, [groupInfo, loading, handleClosePopup, handleJoin]);

   if (error) {
      return <ErrorModal error={error} onClose={handleClosePopup} />;
   }

   return (
      <div className="popup-wrapper group-popup">
         <div className="join-request">
            <div className="join-request-wrapper">
               <div className="popup-container-inner">{mainContent}</div>
            </div>
         </div>
      </div>
   );
});

GroupJoinRequest.displayName = 'GroupJoinRequest';
GroupJoinRequest.propTypes = {
   accessUri: PropTypes.string,
   onClose: PropTypes.func,
};

export default GroupJoinRequest;

